import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import { media, colors } from '../../styles/constants';
import { parseShortcodeArray } from '../../helpers/shortcode';

import EmphasisCircle from './EmphasisCircle';
import TextCircle from './TextCircle';
import SmallCircle from './SmallCircle';
import { CircleSvg, SmallCircleSvg } from './Circles';

const renderCircleBasedOnProps = (circleProps, index) => {
  const { bigtext, smalltext, text, image, color } = circleProps;
  const circleColor = colors[color] ? colors[color] : color;

  if (bigtext && smalltext) {
    const hasBigAlphanumericLastLetter = /\w/g.test(
      bigtext[bigtext.length - 1]
    );

    const lastLetter = hasBigAlphanumericLastLetter
      ? null
      : bigtext[bigtext.length - 1];

    return (
      <EmphasisCircle lastLetter={lastLetter}>
        <CircleSvg color={circleColor} />
        <div>
          <p className="big">
            {hasBigAlphanumericLastLetter ? bigtext : bigtext.slice(0, -1)}
          </p>
          <p className="small">{smalltext}</p>
        </div>
      </EmphasisCircle>
    );
  }

  // The last circle can either have an image or text
  if (text && index !== 3) {
    return (
      <TextCircle>
        <CircleSvg color={circleColor} />
        <p>{text}</p>
      </TextCircle>
    );
  }

  if (image || (text && index === 3)) {
    if (image) {
      return (
        <SmallCircle>
          <SmallCircleSvg color={circleColor} />
          <div>
            <img src={image} />
          </div>
        </SmallCircle>
      );
    } else {
      return (
        <SmallCircle hasText>
          <SmallCircleSvg color={circleColor} />
          <p className="text">{text}</p>
        </SmallCircle>
      );
    }
  }

  throw new Error(`Unable to find matching outcome for ${circleProps}`);
};

const OutcomesContainer = styled.div`
  display: flex;
  height: 48rem;
  width: 100%;
  flex-direction: row;
  flex-wrap: wrap;
  margin-bottom: 13rem;
  margin-top: 5rem;

  > div {
    width: 100%;
  }

  > :first-child,
  > :nth-child(2) {
    > div {
      z-index: 1;
      max-width: 17.3rem;
    }

    @media (min-width: ${media.tabletPortrait}) {
      > div {
        width: 100%;
        max-width: none;
      }

      svg {
        bottom: inherit;
      }
    }
  }

  > :first-child {
    order: 3;
    flex-grow: 0;
    flex-shrink: 1;
    flex-basis: 50%;
    left: -1rem;

    @media (min-width: ${media.tabletPortrait}) {
      left: auto;

      > div {
        left: 0;
      }

      svg {
        top: 0;
      }
    }
  }

  > :nth-child(2) {
    order: 4;
    flex-grow: 0;
    flex-shrink: 1;
    flex-basis: 50%;
    right: -1rem;

    @media (min-width: ${media.tabletPortrait}) {
      right: auto;

      > div {
        margin-top: 23rem;
      }

      svg {
        bottom: 0;
      }
    }
  }

  > :nth-child(3) {
    order: 2;
    flex-grow: 0;
    flex-shrink: 1;
    flex-basis: 100%;
  }

  > :last-child {
    order: 1;
    flex-grow: 0;
    flex-shrink: 1;
    flex-basis: 40%;
    margin-left: auto;
    margin-top: -8rem;

    > div > div {
      width: auto;
    }
  }

  @media (min-width: ${media.tabletPortrait}) {
    flex-direction: row;
    flex-wrap: nowrap;
    height: 58rem;
    margin-bottom: 7rem;

    && > div {
      order: 0;
      flex-grow: 0;
      flex-shrink: 1;
      flex-basis: 30%;

      &:last-child {
        flex-grow: 0;
        flex-shrink: 1;
        flex-basis: 10%;
        margin-top: 0;
      }
    }
  }

  @media (min-width: ${media.tabletLandscape}) {
    height: 60rem;
    margin-bottom: 6rem;
  }

  @media (min-width: ${media.desktop}) {
    height: 67rem;
    width: 100%;
    margin-left: 5rem;
  }

  p {
    color: ${colors.white};
    font-weight: 600;
  }
`;

const Outcomes = ({ circles, isInAboutPage = false }) => {
  let circlesArray = circles;
  if (!isInAboutPage) {
    circlesArray = parseShortcodeArray(circles);
  }

  return (
    <OutcomesContainer>
      {circlesArray.map((circleProps, index) =>
        React.cloneElement(renderCircleBasedOnProps(circleProps, index), {
          key: index
        })
      )}
    </OutcomesContainer>
  );
};

Outcomes.propTypes = {
  circles: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired,
  isInAboutPage: PropTypes.bool
};

export default Outcomes;
